import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimerIcon from '@mui/icons-material/Timer';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import SEO from './SEO';

// 텍스트 정제 함수
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // CSS 코드 패턴 제거
  let cleanedText = text
    .replace(/[;{}:]+/g, ' ') // CSS 구문자 제거
    .replace(/position:\s*absolute/g, '')
    .replace(/top:\s*-?\d+px/g, '')
    .replace(/left:\s*-?\d+px/g, '')
    .replace(/font-size:\s*\d+rem/g, '')
    .replace(/color:\s*#[0-9a-fA-F]{3,6}/g, '')
    .replace(/font-family:\s*[^;]+/g, '')
    .replace(/content:\s*["'][^"']*["']/g, '')
    .replace(/&::before\s*\{[^}]*\}/g, '')
    .replace(/&::after\s*\{[^}]*\}/g, '')
    .replace(/@keyframes\s+[^{]+\{[^}]*\}/g, '')
    .replace(/@media\s+[^{]+\{[^}]*\}/g, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/g, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/g, '')
    .replace(/<[^>]+>/g, '') // HTML 태그 제거
    .replace(/\s+/g, ' ') // 연속된 공백을 하나로 (줄바꿈은 보존)
    .trim();
  
  // 줄바꿈 문자를 실제 줄바꿈으로 변환
  cleanedText = cleanedText
    .replace(/\\n/g, '\n') // 이스케이프된 줄바꿈
    .replace(/\\r\\n/g, '\n') // Windows 줄바꿈
    .replace(/\\r/g, '\n'); // Mac 줄바꿈
  
  // 빈 문자열이거나 의미없는 텍스트인 경우 기본값 반환
  if (!cleanedText || cleanedText.length < 10) {
    return '문장 텍스트를 불러올 수 없습니다.';
  }
  
  return cleanedText;
};

// 키치한 테스트 제목 생성 함수
const generateKitchyTitle = (originalTitle, creatorName, testId = '') => {
  // 사용자가 직접 입력한 제목이 있으면 그대로 사용
  if (originalTitle && !originalTitle.includes('님의 하이라이트 테스트')) {
    return originalTitle;
  }

  // 키치한 제목 템플릿들
  const kitchyTemplates = [
    '제 {N}차 독서 경연대회',
    '제 {N}차 전투력 측정대회',
    '제 {N}차 천하제일 무술대회',
    '제 {N}차 독서왕 선발대회',
    '제 {N}차 책벌레 경마',
    '제 {N}차 독서 서커스',
    '제 {N}차 책 제목 찍기 대회',
    '제 {N}차 독서 카니발',
    '제 {N}차 취향 맞추기'
  ];

  // testId를 기반으로 일관된 템플릿과 회차 선택
  if (testId) {
    const hash = testId.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    // 템플릿 선택
    const templateIndex = Math.abs(hash) % kitchyTemplates.length;
    const template = kitchyTemplates[templateIndex];
    
    // 회차 생성 (1~99회)
    const roundNumber = (Math.abs(hash) % 99) + 1;
    
    return template.replace('{N}', roundNumber);
  }

  // testId가 없으면 랜덤 선택
  const randomTemplateIndex = Math.floor(Math.random() * kitchyTemplates.length);
  const randomTemplate = kitchyTemplates[randomTemplateIndex];
  const randomRound = Math.floor(Math.random() * 99) + 1;
  
  return randomTemplate.replace('{N}', randomRound);
};

const GamePlay = () => {
  const navigate = useNavigate();
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [timerInterval, setTimerInterval] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);

  // 타이머 관리
  useEffect(() => {
    if (timeLeft > 0 && !answered) {
      const interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      setTimerInterval(interval);
      return () => clearInterval(interval);
    } else if (timeLeft === 0 && !answered) {
      // 시간 초과 시 오답 처리
      handleTimeOut();
    }
  }, [timeLeft, answered]);

  // 테스트 데이터 로드
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const testDoc = await getDoc(doc(db, 'tests', testId));
        if (!testDoc.exists()) {
          setError('테스트를 찾을 수 없습니다.');
          return;
        }

        const testData = testDoc.data();
        setTest(testData);

        // 문제 생성
        const generatedQuestions = await generateQuestions(testData);
        setQuestions(generatedQuestions);
      } catch (err) {
        console.error('테스트 로드 오류:', err);
        setError('테스트를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [testId]);

  // 문제 생성 함수
  const generateQuestions = async (testData) => {
    const questions = [];
    
    for (const book of testData.books) {
      try {
        // 해당 책의 하이라이트 가져오기
        const highlightsRef = collection(db, 'highlights');
        const highlightsQuery = query(
          highlightsRef,
          where('bookId', '==', book.isbn),
          where('uid', '==', testData.creatorId)
        );
        const highlightsSnapshot = await getDocs(highlightsQuery);
        
        if (highlightsSnapshot.docs.length > 0) {
          const highlights = highlightsSnapshot.docs.map(doc => doc.data());
          
          // 책당 문제 수 결정 (하이라이트 수와 설정된 문제 수 중 작은 값)
          const questionsForThisBook = Math.min(
            book.questionsPerBook || 1, // Firestore에 저장된 값 사용
            highlights.length
          );
          
          // 랜덤하게 하이라이트 선택
          const shuffledHighlights = highlights.sort(() => 0.5 - Math.random());
          const selectedHighlights = shuffledHighlights.slice(0, questionsForThisBook);
          
          for (const highlight of selectedHighlights) {
            // 하이라이트 텍스트 정제
            const cleanedText = sanitizeText(highlight.text);
            
            // 텍스트가 유효한 경우에만 문제 생성
            if (cleanedText && cleanedText.length > 10) {
              // 선택지 생성 (정답 + 오답 3개)
              const otherBooks = testData.books.filter(b => b.id !== book.id);
              const wrongAnswers = otherBooks
                .sort(() => 0.5 - Math.random())
                .slice(0, 3);

              const choices = [
                { ...book, isCorrect: true },
                ...wrongAnswers.map(b => ({ ...b, isCorrect: false }))
              ].sort(() => 0.5 - Math.random());

              questions.push({
                highlight: { ...highlight, text: cleanedText },
                correctBook: book,
                choices: choices,
                questionNumber: questions.length + 1
              });
            }
          }
        }
      } catch (err) {
        console.error('문제 생성 오류:', err);
      }
    }

    // 전체 문제 배열을 랜덤하게 섞어서 같은 책이 연속되지 않도록 함
    const shuffledQuestions = questions.sort(() => 0.5 - Math.random());
    
    // 섞인 후 문제 번호 재할당
    shuffledQuestions.forEach((question, index) => {
      question.questionNumber = index + 1;
    });

    return shuffledQuestions;
  };

  // 시간 초과 처리
  const handleTimeOut = () => {
    if (answered || !questions || questions.length === 0) return;

    setAnswered(true);
    clearInterval(timerInterval);

    // 시간 초과는 오답으로 처리 (점수 증가 없음)
    console.log('시간 초과 - 오답 처리');

    setTimeout(() => {
      try {
        if (currentQuestion < questions.length - 1) {
          // 다음 문제가 있는 경우
          setCurrentQuestion(prev => prev + 1);
          setTimeLeft(15);
          setAnswered(false);
          setSelectedCardId(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // 마지막 문제인 경우 테스트 완료
          handleTestComplete();
        }
      } catch (error) {
        console.error('시간 초과 처리 중 오류:', error);
        // 오류 발생 시에도 테스트 완료로 처리
        handleTestComplete();
      }
    }, 500);
  };

  // 답변 처리
  const handleAnswer = (selectedBook) => {
    if (answered || !questions || questions.length === 0) return;

    setAnswered(true);
    setSelectedCardId(selectedBook?.id); // null 체크 추가
    clearInterval(timerInterval);

    try {
      const currentQ = questions[currentQuestion];
      const isCorrect = selectedBook?.id === currentQ.correctBook.id;
      
      if (isCorrect) {
        setScore(prev => prev + 1);
      }
    } catch (error) {
      console.error('답변 처리 중 오류:', error);
    }

    setTimeout(() => {
      try {
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
          setTimeLeft(15);
          setAnswered(false);
          setSelectedCardId(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // 테스트 완료
          handleTestComplete();
        }
      } catch (error) {
        console.error('다음 문제 이동 중 오류:', error);
        // 오류 발생 시에도 테스트 완료로 처리
        handleTestComplete();
      }
    }, 500);
  };

  // 테스트 완료 처리
  const handleTestComplete = async () => {
    try {
      // 결과 저장
      const resultData = {
        testId: testId,
        score: score,
        totalQuestions: questions.length,
        accuracy: Math.round((score / questions.length) * 100),
        completedAt: serverTimestamp(),
        playerName: auth.currentUser?.displayName || '익명',
        playerId: auth.currentUser?.uid || null
      };

      await addDoc(collection(db, 'testResults'), resultData);

      // 참여자 수 업데이트
      const testRef = doc(db, 'tests', testId);
      await updateDoc(testRef, {
        participantCount: (test.participantCount || 0) + 1
      });

      setShowResult(true);
    } catch (err) {
      console.error('결과 저장 오류:', err);
    }
  };

  // 공유 처리
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/game/play/${testId}`;
    const shareText = `얕은 지식을 자랑할 기회입니다`;
    // Web Share API 지원 확인 및 모바일 환경 감지
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const supportsShare = navigator.share && typeof navigator.share === 'function';
    
    if (supportsShare && isMobile) {
      try {
        await navigator.share({
          title: generateKitchyTitle(test?.title, test?.creatorName, testId),
          text: shareText,
          url: shareUrl
        });
        // 공유 성공 시 다이얼로그 표시
        setShowShareDialog(true);
      } catch (error) {
        console.log('공유 취소 또는 에러:', error);
        // 공유가 취소되었거나 에러 발생 시 클립보드로 대체
        fallbackToClipboard(shareUrl);
      }
    } else {
      // Web Share API를 지원하지 않거나 데스크톱인 경우 클립보드 사용
      fallbackToClipboard(shareUrl);
    }
  };

  // 클립보드 대체 방법
  const fallbackToClipboard = async (url) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setShowShareDialog(true);
      } else {
        // 구형 브라우저 지원
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setShowShareDialog(true);
        } catch (err) {
          console.error('클립보드 복사 실패:', err);
          alert('링크를 복사할 수 없습니다. 수동으로 복사해주세요: ' + url);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('클립보드 에러:', error);
      alert('링크를 복사할 수 없습니다. 수동으로 복사해주세요: ' + url);
    }
  };

  // 결과 화면 상단에 추가
  const getResultComment = (accuracy) => {
    if (accuracy >= 80) {
      return {
        title: '투시 안경 쓰셨나요?\n 취향이 완벽하게 통해요🔥',
        desc: '주인장과 관심사가 찰떡🍢\n결과를 자랑해보세요!'
      };
    } else if (accuracy >= 60) {
      return {
        title: '취향이 꽤나 비슷한데요?😎',
        desc: '주인장과 통하는 부분이 많아요! \n한 번 더 도전해보는건 어떠세요?'
      };
    } else if (accuracy >= 40) {
      return {
        title: '아직은 어색한 사이...인가요?🤔',
        desc: '주인장과의 취향이 조금은 다른가봐요! 아니면 다시 도전해보는 건 어떠세요?'
      };
    } else {
      return {
        title: '전투력 상승이 필요합니다!💪',
        desc: '주인장과 친해지려면 한참 멀었어요!\n결과는 공유하지 않는 걸 권장함'
      };
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#fafafa',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <Box
          sx={{
            width: { xs: 70, sm: 80 },
            height: { xs: 70, sm: 80 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 8px 32px rgba(26, 35, 126, 0.2)'
          }}
        >
          <CircularProgress size={40} sx={{ color: 'white' }} />
        </Box>
        <Typography
          sx={{
            color: '#666',
            fontFamily: 'NanumSquareR',
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}
        >
          테스트를 준비하고 있어요...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: '#fafafa',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <Box
          sx={{
            width: { xs: 70, sm: 80 },
            height: { xs: 70, sm: 80 },
            borderRadius: '50%',
            bgcolor: '#ffebee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            border: '2px solid #f44336'
          }}
        >
          <CloseIcon sx={{ color: '#f44336', fontSize: { xs: '1.8rem', sm: '2rem' } }} />
        </Box>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            fontFamily: 'NanumSquareR',
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
        >
          {error}
        </Alert>
        <Button 
          onClick={() => navigate('/')}
          variant="contained"
          sx={{
            bgcolor: '#1a237e',
            color: 'white',
            fontWeight: 600,
            borderRadius: '20px',
            px: { xs: 3, sm: 4 },
            py: { xs: 1.2, sm: 1.5 },
            textTransform: 'none',
            fontFamily: 'NanumSquareB',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            '&:hover': {
              bgcolor: '#283593'
            }
          }}
        >
          홈으로 돌아가기
        </Button>
      </Box>
    );
  }

  if (showResult) {
    const accuracy = Math.round((score / questions.length) * 100);
    const resultComment = getResultComment(accuracy);
    return (
      <Box sx={{ 
        maxWidth: '500px', 
        margin: '0 auto', 
        minHeight: '100vh',
        bgcolor: '#fafafa'
      }}>
        <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          {/* 결과 원형 차트 */}
          <Box
            sx={{
              width: { xs: 120, sm: 140 },
              height: { xs: 120, sm: 140 },
              borderRadius: '50%',
              background: `conic-gradient(#1a237e 0deg ${(score / questions.length) * 360}deg, #e0e0e0 ${(score / questions.length) * 360}deg 360deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(26, 35, 126, 0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '80%',
                height: '80%',
                borderRadius: '50%',
                bgcolor: 'white',
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: '#1a237e',
                fontWeight: 800,
                fontFamily: 'NanumSquareB',
                position: 'relative',
                zIndex: 1,
                fontSize: { xs: '2rem', sm: '2.5rem' }
              }}
            >
              {accuracy}%
            </Typography>
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontFamily: 'NanumSquareEB',
              color: '#333',
              fontSize: { xs: '1.5rem', sm: '1.8rem' }
            }}
          >
            테스트 완료!
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.2rem' },
              mb: 4,
              fontFamily: 'NanumSquareR',
              color: '#666',
              lineHeight: 1.6
            }}
          >
          {questions.length}문제 중 {score}문제 정답!
          </Typography>

          {/* 키치한 결과 메시지 */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: 4,
              borderRadius: '16px',
              bgcolor: 'white',
              border: '1px solid #f0f0f0',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
            }}
          >
            <Typography
              sx={{
                color: '#1a237e',
                fontFamily: 'NanumSquareB',
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                fontWeight: 700,
                mb: 1.5,
                whiteSpace: 'pre-line',
                lineHeight: 1.4
              }}
            >
              {resultComment.title}
            </Typography>
            <Typography
              sx={{
                color: '#495057',
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '0.98rem', sm: '1.05rem' },
                whiteSpace: 'pre-line',
                lineHeight: 1.7
              }}
            >
              {resultComment.desc}
            </Typography>
          </Paper>

          {/* 액션 버튼 */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#1a237e',
                color: '#1a237e',
                fontWeight: 600,
                borderRadius: '24px',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#283593',
                  bgcolor: 'rgba(26, 35, 126, 0.04)',
                  borderWidth: '2px'
                }
              }}
            >
              홈으로
            </Button>
            <Button
              variant="contained"
              onClick={handleShare}
              startIcon={<ShareIcon />}
              sx={{
                bgcolor: '#FFD700',
                color: '#1a237e',
                fontWeight: 700,
                borderRadius: '24px',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                boxShadow: '0 4px 16px rgba(255, 215, 0, 0.3)',
                '&:hover': {
                  bgcolor: '#FFC107',
                  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              친구에게 공유하기
            </Button>
          </Box>
        </Box>

        <Dialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              maxWidth: '400px'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#1a237e', 
            color: 'white',
            fontWeight: 600,
            fontFamily: 'NanumSquareB',
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }}>
            링크 복사 완료
            <IconButton
              onClick={() => setShowShareDialog(false)}
              sx={{ 
                position: 'absolute', 
                right: 8, 
                top: 8,
                color: 'white'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: { xs: 50, sm: 60 },
                  height: { xs: 50, sm: 60 },
                  borderRadius: '50%',
                  bgcolor: '#e8f5e8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <CheckIcon sx={{ color: '#4caf50', fontSize: { xs: '1.8rem', sm: '2rem' } }} />
              </Box>
              <Typography sx={{ 
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                color: '#333'
              }}>
                테스트 링크가 클립보드에 복사되었습니다!
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <>
      <SEO 
        title={`${generateKitchyTitle(test?.title, test?.creatorName, testId)} | 스냅로그`}
        description="얕은 지식을 자랑할 기회입니다."
        url={`/game/play/${testId}`}
        image="/op-image-game.png"
      />
      
      <Box sx={{ 
        maxWidth: '500px', 
        margin: '0 auto', 
        minHeight: '100vh',
        bgcolor: '#fafafa',
        position: 'relative'
      }}>
        {/* 헤더 - 크기 축소 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2, 
          bgcolor: 'white',
          borderBottom: '1px solid #f0f0f0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: '52px'
        }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              mr: 1.5,
              color: '#666',
              p: 0.5,
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                fontFamily: 'NanumSquareB',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                color: '#333',
                lineHeight: 1.2
              }}
            >
              {generateKitchyTitle(test?.title, test?.creatorName, testId)}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#999',
                fontFamily: 'NanumSquareR',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              {currentQuestion + 1} / {questions.length}
            </Typography>
          </Box>
        </Box>

        {/* 진행률 바 */}
        <LinearProgress
          variant="determinate"
          value={((currentQuestion + 1) / questions.length) * 100}
          sx={{
            height: 2,
            bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#1a237e',
              borderRadius: '1px'
            },
          }}
        />

        {/* 메인 컨텐츠 - 패딩 중복 제거 */}
        <Box sx={{ pb: 8, pt: 2 }}>
          {/* 질문 제목 */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontFamily: 'NanumSquareB',
              lineHeight: 1.4,
              color: '#333',
              fontSize: { xs: '1.2rem', sm: '1.4rem' },
              px: 2,
              pt: 2
            }}
          >
            문장을 보고 <br />책을 맞춰 보세요
          </Typography>
            
          {/* 문장 박스 */}
          <Box
            sx={{
              bgcolor: '#ffffff',
              p: 2,
              position: 'relative',
              mx: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.7,
                fontFamily: 'NanumSquareM',
                color: '#495057',
                pl: 2,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                whiteSpaceCollapse: 'preserve',
                textAlign: 'left'
              }}
            >
              {currentQ.highlight.text}
            </Typography>
          </Box>

          {/* 선택지 */}
          <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
            {currentQ.choices.map((choice, index) => (
              <Card
                key={choice.id}
                elevation={0}
                sx={{
                  border: '1.5px solid #e0e0e0',
                  borderRadius: '12px',
                  cursor: answered ? 'default' : 'pointer',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: 'white',
                  boxShadow: selectedCardId === choice.id ? '0 6px 24px rgba(26,35,126,0.10)' : '0 2px 8px rgba(0,0,0,0.04)',
                  transform: selectedCardId === choice.id ? 'scale(1.03)' : 'scale(1)',
                  opacity: selectedCardId && selectedCardId !== choice.id ? 0.7 : 1,
                  '&:hover': !answered ? {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 16px rgba(26,35,126,0.13)',
                    borderColor: '#1a237e'
                  } : {},
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => !answered && handleAnswer(choice)}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CardMedia
                      component="img"
                      sx={{
                        width: { xs: 45, sm: 50 },
                        height: { xs: 68, sm: 75 },
                        borderRadius: '6px',
                        mr: { xs: 2, sm: 2.5 },
                        objectFit: 'cover',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #f0f0f0'
                      }}
                      image={choice.thumbnail || '/images/no-cover.png'}
                      alt={`${choice.title}${choice.authors ? ` - ${choice.authors.join(', ')}` : ''}`}
                      loading="lazy"
                    />
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontFamily: 'NanumSquareB',
                          color: '#333',
                          fontSize: { xs: '0.9rem', sm: '0.95rem' },
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {choice.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontFamily: 'NanumSquareR',
                          fontSize: { xs: '0.8rem', sm: '0.85rem' }
                        }}
                      >
                        {choice.authors?.join(', ') || '저자 정보 없음'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* 하단 고정 타이머 - 토스트 스타일 */}
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: timeLeft <= 5 ? '#fff3e0' : 'rgba(0,0,0,0.8)',
            color: timeLeft <= 5 ? '#ff9800' : 'white',
            px: { xs: 2.5, sm: 3 },
            py: { xs: 1.2, sm: 1.5 },
            borderRadius: '24px',
            border: timeLeft <= 5 ? '1px solid #ff9800' : 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(10px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: { xs: '70px', sm: '80px' },
            justifyContent: 'center'
          }}
        >
          <TimerIcon sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            color: 'inherit'
          }} />
          <Typography
            sx={{
              fontWeight: 700,
              fontFamily: 'NanumSquareB',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              color: 'inherit'
            }}
          >
            {timeLeft}s
          </Typography>
        </Box>

        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(16px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
        `}</style>
      </Box>
    </>
  );
};

export default GamePlay; 